// TES-516 keeps the existing offline mutation flow and only normalizes image metadata at its cache boundary.
// fallow-ignore-file complexity
// TES-516 touches the existing upload loop clone without changing the upload responsibilities.
// fallow-ignore-file code-duplication
/**
 * Defect mutation hooks with offline support
 *
 * Wraps defect create/update operations with useOfflineMutation
 * so they work both online and offline (when offline-mode flag is enabled).
 *
 * Images are stored as Base64 in IndexedDB when offline and uploaded during sync.
 */

import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { useOfflineMutation } from "./useOfflineMutation";
import { storeImage } from "@/lib/offlineStore";
import { compressImage, COMPRESSION_PRESETS, formatBytes } from "@/lib/imageCompression";
import { notifyDefectCreated, notifyAssignment } from "@/lib/notifications";
import { defectsKeys, offlinePayloadToDefect, type DefectWithDetails } from "./useDefectsQuery";
import { itemDefectsKeys } from "./useItemDefectsQuery";
import { normalizeDefectImagePaths } from "@/lib/defectImages";
import { logger } from "@/lib/logger";

/**
 * Extract a non-PII RLS/Postgres error signal from a Supabase mutation error.
 * Returns only the structured code/status (never the message body, which can
 * contain row values). PostgREST surfaces RLS denials as code `42501`.
 */
function defectErrorSignal(error: unknown): {
  code: string | null;
  status: number | null;
  rlsDenied: boolean;
} {
  const e = (error ?? {}) as { code?: unknown; status?: unknown };
  const code = typeof e.code === "string" ? e.code : null;
  const status = typeof e.status === "number" ? e.status : null;
  // RLS denial: Postgres insufficient_privilege (42501) or PostgREST 401/403.
  const rlsDenied = code === "42501" || status === 401 || status === 403;
  return { code, status, rlsDenied };
}

export interface CreateDefectInput {
  title: string;
  description: string | null;
  severity: "low" | "medium" | "high" | "critical";
  status?: "open" | "in_progress" | "resolved" | "closed" | "cancelled";
  assignedTo: string | null;
  /** Fachabteilung-Gruppe (TES-1881/1860, v2.3 §5) — Pflicht-Zuordnung in der In-Context-Erfassung. */
  assignedGroupId?: string | null;
  dueDate: string | null;
  inspectionId?: string | null;
  checklistItemId?: string | null;
  /** Optional defect category (FK defect_category, TES-1693). */
  categoryId?: string | null;
  /** Optional Prüfobjekt-Anker (FK inspection_positions) — baubegleitende Erfassung am Prüfobjekt, TES-1767. */
  positionId?: string | null;
  imageFiles: File[];
  /** Base64 previews for offline storage */
  imagePreviews?: string[];
}

interface UpdateDefectInput {
  id: string;
  title: string;
  description: string | null;
  severity: "low" | "medium" | "high" | "critical";
  status: "open" | "in_progress" | "resolved" | "closed" | "cancelled";
  assignedTo: string | null;
  /** Fachabteilung-Gruppe (TES-1868, v2.3 §5) — Pflicht-Zuordnung; assigned_to bleibt optional. */
  assignedGroupId?: string | null;
  dueDate: string | null;
  teamId?: string | null;
  createdBy?: string | null;
  verifyingQm?: string | null;
  imageFiles: File[];
  imagePreviews?: string[];
  existingImages: string[];
}

const UPLOAD_TIMEOUT_MS = 30_000;

async function uploadDefectImages(
  imageFiles: File[],
  tenantId: string,
  pathPrefix: string,
): Promise<string[]> {
  const uploadedPaths: string[] = [];

  for (const file of imageFiles) {
    try {
      const {
        file: compressedFile,
        wasCompressed,
        originalSize,
        compressedSize,
      } = await compressImage(file, COMPRESSION_PRESETS.defect);

      if (wasCompressed) {
        logger.debug(
          `Defect image compressed: ${formatBytes(originalSize)} → ${formatBytes(compressedSize)} (${Math.round((1 - compressedSize / originalSize) * 100)}% saved)`,
        );
      }

      const extension = compressedFile.name.split(".").pop() || "webp";
      const fileName = `${tenantId}/${pathPrefix}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${extension}`;

      // Race upload against timeout to prevent indefinite hangs
      const uploadPromise = supabase.storage
        .from("checklist-images")
        .upload(fileName, compressedFile);

      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error(`Upload timeout after ${UPLOAD_TIMEOUT_MS / 1000}s`)),
          UPLOAD_TIMEOUT_MS,
        ),
      );

      const { data: uploadData, error: uploadError } = await Promise.race([
        uploadPromise,
        timeoutPromise,
      ]);

      if (uploadError) {
        logger.error("Defect image upload error", {
          pathPrefix,
          ...defectErrorSignal(uploadError),
        });
        continue;
      }

      uploadedPaths.push(uploadData.path);
    } catch (err) {
      logger.error("Defect image upload failed (timeout or error), skipping", {
        pathPrefix,
        ...defectErrorSignal(err),
      });
      continue;
    }
  }

  logger.info("Defect image upload batch complete", {
    pathPrefix,
    requested: imageFiles.length,
    uploaded: uploadedPaths.length,
  });

  return uploadedPaths;
}

/**
 * Hook for creating defects with offline support.
 * When offline, images are stored as Base64 in IndexedDB and the mutation is queued.
 */
export function useUpdateDefectMutation() {
  const queryClient = useQueryClient();

  return useOfflineMutation<void, Error, UpdateDefectInput>({
    entityType: "defect",
    operation: "UPDATE",

    getImageRefs: (input) =>
      (input as UpdateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs || [],

    mutationFn: async (input) => {
      logger.info("Defect update requested", {
        defectId: input.id,
        severity: input.severity,
        status: input.status,
        newImageCount: input.imageFiles.length,
      });

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("active_tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!profile?.active_tenant_id) {
        throw new Error("No active tenant – cannot upload images");
      }
      const tenantId = profile.active_tenant_id;

      // Upload new images
      const uploadedPaths = await uploadDefectImages(input.imageFiles, tenantId, "defects");
      const allImages = [...input.existingImages, ...uploadedPaths];

      const updateData: TablesUpdate<"defects"> = {
        title: input.title,
        description: input.description,
        severity: input.severity,
        status: input.status,
        assigned_to: input.assignedTo,
        images: allImages.length > 0 ? allImages : null,
        due_date: input.dueDate,
        updated_at: new Date().toISOString(),
      };

      // TES-1868: Fachabteilung-Gruppe nur schreiben, wenn der Dialog sie liefert (undefined = unverändert).
      // TES-2063: assigned_group_id ist NOT NULL (Mig 362) — null darf nie gesendet werden, daher
      // schreiben wir ausschließlich einen echten Gruppen-Wert (`!= null` schließt null UND undefined aus).
      if (input.assignedGroupId != null) {
        updateData.assigned_group_id = input.assignedGroupId;
      }

      if (input.teamId) {
        updateData.team_id = input.teamId;
      }

      if (input.createdBy) {
        updateData.created_by = input.createdBy;
        // TES-1141: no creator hard-default for verifying_qm on edit either —
        // the verifier only changes when explicitly chosen (or at verify time,
        // when the acting QM attributes themselves).
      }
      if (input.verifyingQm) {
        updateData.verifying_qm = input.verifyingQm;
      }

      // Request an exact row count so an RLS/permission denial that returns
      // "0 rows affected" with NO error is not mistaken for a success. Mirrors
      // the zero-row denial detection in src/lib/syncEntryHandlers.ts:178-183.
      const { error, count } = await supabase
        .from("defects")
        .update(updateData, { count: "exact" })
        .eq("id", input.id);

      if (error) {
        const signal = defectErrorSignal(error);
        logger.error("Defect update failed", {
          defectId: input.id,
          ...signal,
        });
        if (signal.rlsDenied) {
          logger.warn("Defect update denied by RLS", {
            defectId: input.id,
            code: signal.code,
            status: signal.status,
          });
        }
        throw error;
      }

      // No error but 0 rows changed = the row is outside the caller's RLS scope
      // (or no longer exists). Surface it as a denial/not-found signal instead of
      // logging a false success. Behavior is otherwise unchanged (no throw).
      if (count === 0) {
        logger.warn("Defect update denied/not-found (RLS)", {
          defectId: input.id,
          rlsDenied: true,
          count: 0,
        });
        return;
      }

      logger.info("Defect update succeeded", {
        defectId: input.id,
        status: input.status,
        imageCount: allImages.length,
      });
    },

    prepareOffline: async (input) => {
      if (input.imagePreviews?.length) {
        const refs: string[] = [];
        for (const preview of input.imagePreviews) {
          const id = `offline-img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          refs.push(id);
          await storeImage(id, preview);
        }
        (input as UpdateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs = refs;
      }
    },

    toPayload: (input) => {
      const imageRefs =
        (input as UpdateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs || [];

      return {
        id: input.id,
        title: input.title,
        description: input.description,
        severity: input.severity,
        status: input.status,
        assigned_to: input.assignedTo,
        ...(input.assignedGroupId !== undefined
          ? { assigned_group_id: input.assignedGroupId }
          : {}),
        due_date: input.dueDate,
        updated_at: new Date().toISOString(),
        existing_images: input.existingImages || [],
        images: input.imagePreviews?.length
          ? [...(input.existingImages || []), ...input.imagePreviews]
          : input.existingImages?.length
            ? input.existingImages
            : null,
        created_by: input.createdBy || null,
        verifying_qm: input.verifyingQm || null,
        _imageRefs: imageRefs,
      };
    },

    onOfflineQueued: (payload) => {
      const updateCache = (old: DefectWithDetails[] | undefined) => {
        if (!old) return old;
        return old.map((defect) =>
          defect.id === payload.id
            ? {
                ...defect,
                title: payload.title as string,
                description: (payload.description ??
                  defect.description) as typeof defect.description,
                severity: (payload.severity ?? defect.severity) as typeof defect.severity,
                status: (payload.status ?? defect.status) as typeof defect.status,
                assigned_to: (payload.assigned_to ?? null) as string | null,
                due_date: (payload.due_date ?? null) as string | null,
                images: normalizeDefectImagePaths(payload.images),
                updated_at: payload.updated_at as string,
                isOfflinePending: true,
              }
            : defect,
        );
      };

      queryClient.setQueriesData<DefectWithDetails[]>({ queryKey: defectsKeys.all }, updateCache);
      queryClient.setQueriesData<DefectWithDetails[]>(
        { queryKey: itemDefectsKeys.all },
        updateCache,
      );
    },

    invalidateKeys: [
      defectsKeys.all as unknown as string[],
      itemDefectsKeys.all as unknown as string[],
    ],

    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: defectsKeys.all });
        queryClient.invalidateQueries({ queryKey: itemDefectsKeys.all });
      },
    },
  });
}

export function useCreateDefectMutation() {
  const queryClient = useQueryClient();

  return useOfflineMutation<{ id: string }, Error, CreateDefectInput>({
    entityType: "defect",
    operation: "INSERT",

    getImageRefs: (input) =>
      (input as CreateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs || [],

    mutationFn: async (input) => {
      logger.info("Defect create requested", {
        severity: input.severity,
        status: input.status ?? "open",
        inspectionScoped: !!input.inspectionId,
        newImageCount: input.imageFiles.length,
      });

      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data: profile } = await supabase
        .from("profiles")
        .select("active_tenant_id")
        .eq("user_id", user.id)
        .single();

      if (!profile?.active_tenant_id) {
        throw new Error("No active tenant – cannot upload images");
      }
      const tenantId = profile.active_tenant_id;
      const pathPrefix = input.inspectionId
        ? `inspections/${input.inspectionId}/${input.checklistItemId}`
        : "defects";

      const uploadedPaths = await uploadDefectImages(input.imageFiles, tenantId, pathPrefix);

      // team_id is set automatically by the DB trigger auto_set_team_on_operative_insert()
      // which is tenant-aware since Migration 230 (Cross-Tenant Team Guard).
      // DO NOT set team_id manually from the frontend — see Antipattern 015.
      // TES-1141: verifying_qm is NOT set here. The DB trigger
      // set_default_verifying_qm (migration 264) assigns it only when the
      // creator holds the QM role; otherwise it stays NULL so any team QM
      // can verify in one step (no creator hard-default).
      // team_id is intentionally omitted (set by the DB trigger, see above), so we type
      // the payload without it and bridge to the full Insert type via `unknown`.
      // position_id (TES-1767, Migration 323) — bis types.ts nach dem Deploy regeneriert ist,
      // ist die Spalte noch nicht in TablesInsert<"defects">; forward-kompatibel ergänzt.
      // TES-2063: assigned_group_id ist NOT NULL (Mig 362), wird aber vom DB-Trigger
      // default_assigned_group bei NULL/Omit auf die Tenant-Fallback-Gruppe geheilt (analog dem
      // team_id-Trigger) — daher hier optional/nullable typisiert. Die Pflicht-Auswahl erzwingt das
      // Frontend fachlich im Picker; der DB-Trigger ist nur der Backstop für Alt-/Offline-Inserts.
      const insertPayload: Omit<TablesInsert<"defects">, "team_id" | "assigned_group_id"> & {
        position_id: string | null;
        assigned_group_id?: string | null;
      } = {
        title: input.title,
        description: input.description,
        severity: input.severity,
        status: input.status || "open",
        assigned_to: input.assignedTo,
        assigned_group_id: input.assignedGroupId ?? null,
        due_date: input.dueDate,
        inspection_id: input.inspectionId || null,
        checklist_item_id: input.checklistItemId || null,
        category_id: input.categoryId ?? null,
        position_id: input.positionId ?? null,
        created_by: user?.id || null,
        images: uploadedPaths.length > 0 ? uploadedPaths : null,
      };
      const { data, error } = await supabase
        .from("defects")
        .insert(insertPayload as unknown as TablesInsert<"defects">)
        .select("id")
        .single();

      if (error) {
        const signal = defectErrorSignal(error);
        logger.error("Defect create failed", {
          severity: input.severity,
          ...signal,
        });
        if (signal.rlsDenied) {
          logger.warn("Defect create denied by RLS", {
            code: signal.code,
            status: signal.status,
          });
        }
        throw error;
      }

      // `.select("id").single()` returns the inserted row. A denial normally
      // surfaces as an error above, but guard the no-error/no-row case so a
      // blind "succeeded" log can never fire on a non-insert.
      if (!data) {
        logger.warn("Defect create denied/not-found (RLS)", {
          severity: input.severity,
          rlsDenied: true,
          count: 0,
        });
        throw new Error("Defect insert returned no row");
      }

      logger.info("Defect create succeeded", {
        defectId: data.id ?? null,
        severity: input.severity,
        imageCount: uploadedPaths.length,
      });

      // Notifications (only when online; `data` is guaranteed non-null here).
      if (input.assignedTo) {
        notifyAssignment(input.assignedTo, "defect", input.title, data.id);
      }

      // Server-side fan-out: Edge Function resolves all QM recipients
      notifyDefectCreated("__all_qms__", input.title, data.id, input.severity);

      return data;
    },

    prepareOffline: async (input) => {
      if (input.imagePreviews?.length) {
        const refs: string[] = [];
        for (const preview of input.imagePreviews) {
          const id = `offline-img-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
          refs.push(id);
          await storeImage(id, preview);
        }
        // Attach refs so toPayload can read them synchronously
        (input as CreateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs = refs;
      }
    },

    toPayload: (input) => {
      const imageRefs =
        (input as CreateDefectInput & { _offlineImageRefs?: string[] })._offlineImageRefs || [];

      return {
        id: crypto.randomUUID(),
        title: input.title,
        description: input.description,
        severity: input.severity,
        status: input.status || "open",
        assigned_to: input.assignedTo,
        // TES-2026: seit Mig 332/362 traegt jeder Mangel eine Gruppen-Zuordnung —
        // der Online-Insert fuehrt sie, der Offline-Replay (syncEngine schickt dieses
        // Payload 1:1 als Insert) darf sie nicht verlieren.
        assigned_group_id: input.assignedGroupId ?? null,
        due_date: input.dueDate,
        inspection_id: input.inspectionId || null,
        checklist_item_id: input.checklistItemId || null,
        category_id: input.categoryId ?? null,
        position_id: input.positionId ?? null,
        images: input.imagePreviews?.length ? input.imagePreviews : null,
        _imageRefs: imageRefs,
      };
    },

    onOfflineQueued: (payload) => {
      // Optimistically add the offline defect to all defect list caches
      queryClient.setQueriesData<DefectWithDetails[]>({ queryKey: defectsKeys.all }, (old) => {
        if (!old) return old;
        const stub = offlinePayloadToDefect(payload);
        return [stub, ...old];
      });
    },

    invalidateKeys: [
      defectsKeys.all as unknown as string[],
      itemDefectsKeys.all as unknown as string[],
    ],

    mutationOptions: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: defectsKeys.all });
        queryClient.invalidateQueries({ queryKey: itemDefectsKeys.all });
      },
    },
  });
}
